/**
 * Feature Icon Mapping
 * 
 * Icon names are stored in keywords_cz database as the 'icon' field.
 * This file maps icon names (strings) to actual Lucide icon components.
 * 
 * React components cannot be stored in a database, so we store the icon NAME
 * in the database and look up the component here.
 * 
 * This is the only place where icon components should be imported.
 */

import {
  Car,
  Fence,
  Building2,
  Sofa,
  PawPrint,
  Warehouse,
  UtensilsCrossed,
  WashingMachine,
  Wind,
  Package,
  Tv,
  Wifi,
  Bed,
  Armchair,
  Bath,
  ShowerHead,
  Flame,
  Thermometer,
  Bike,
  DoorOpen,
  Video,
  Shield,
  Dumbbell,
  Sparkles,
  Sun,
  Volume2,
  Microwave,
  Coffee,
  Refrigerator,
  CookingPot,
  type LucideIcon,
} from 'lucide-react'

/**
 * Map of icon names (strings) to Lucide icon components.
 * Icon names are stored in keywords_cz database.
 */
const ICON_REGISTRY: Record<string, LucideIcon> = {
  // Transport & Parking
  Car,
  Warehouse,
  Bike,
  
  // Building & Structure
  Building2,
  Fence,
  DoorOpen,
  
  // Furniture & Home
  Sofa,
  Bed,
  Armchair,
  
  // Appliances
  UtensilsCrossed,
  WashingMachine,
  Microwave,
  CookingPot,
  Refrigerator,
  Coffee,
  
  // Utilities
  Wifi,
  Tv,
  Wind,
  Flame,
  Thermometer,
  
  // Bathroom
  Bath,
  ShowerHead,
  
  // Security & Access
  Video,
  Shield,
  
  // Storage
  Package,
  
  // Amenities
  Dumbbell,
  PawPrint,
  
  // Environment
  Sun,
  Volume2,
  
  // Fallback
  Sparkles,
}

/**
 * Get icon component from icon name (stored in database).
 * Falls back to Sparkles if icon not found.
 * 
 * @param iconName - Icon name from keywords_cz database (e.g., "Fence", "Car")
 * @returns Lucide icon component
 */
export function getIconComponent(iconName: string | undefined): LucideIcon {
  if (!iconName) return Sparkles
  return ICON_REGISTRY[iconName] || Sparkles
}

/**
 * @deprecated Use getIconComponent with icon name from database instead.
 * This function is kept for backward compatibility only.
 * 
 * Get icon for a feature key using hardcoded fallback mapping.
 * This is only used if keywords_cz doesn't have an icon defined.
 */
export function getFeatureIcon(featureKey: string): LucideIcon {
  // Fallback mappings if database doesn't have icon defined
  const FALLBACK_MAP: Record<string, string> = {
    furnished: 'Sofa',
    pets_allowed: 'PawPrint',
    balcony: 'Fence',
    parking: 'Car',
    dishwasher: 'UtensilsCrossed',
    washing_machine: 'WashingMachine',
    dryer: 'Wind',
  }
  
  const iconName = FALLBACK_MAP[featureKey]
  return getIconComponent(iconName)
}

